/**
 * Aktor-nyilvántartás: létrehozás, törlés, térbeli lekérdezés, sebzés.
 *
 * A sebzés SZÁNDÉKOSAN itt van és nem a harci modulban: így pontosan egy
 * helyen dől el, mi számít halálnak, mikor szól a `damage`/`death` esemény,
 * és nem fordulhat elő, hogy két rendszer különbözőképpen öl meg valakit.
 *
 * Gazda: C
 */

import * as THREE from 'three'
import type {
  Actor,
  ActorHit,
  ActorsApi,
  DamageKind,
  EntityId,
  Faction,
  GameEventMap,
  SpawnSpec,
} from './contracts'
import type { EventBus } from './events'

const _tmp = new THREE.Vector3()

export class ActorStore implements ActorsApi {
  private list: Actor[] = []
  private byId = new Map<EntityId, Actor>()
  private nextId = 1
  private playerRef: Actor | null = null
  private readonly events: EventBus<GameEventMap>

  /** Páncél-elnyelés: a sebzés hány százalékát viszi el a páncél. */
  armorAbsorb = 0.33
  /** A játékos páncélját ez a callback olvassa/írja (a player rendszer adja). */
  armorHook: { get(): number; consume(n: number): void } | null = null

  /**
   * Látóvonal-ellenőrzés a területi sebzéshez. A pályabetöltés köti be.
   *
   * Enélkül a robbanás ÁTMEGY A FALON: a `splash` csak távolságot nézne, és
   * a fedezék mögé húzódó játékost is eltalálná. Ez a legrosszabb fajta
   * igazságtalanság, mert pont azt a viselkedést bünteti, amit a játék
   * egyébként tanít (fedezékbe menni a telegráf alatt).
   */
  lineOfSight: ((from: THREE.Vector3, to: THREE.Vector3) => boolean) | null = null

  constructor(events: EventBus<GameEventMap>) {
    this.events = events
  }

  get player(): Actor {
    if (!this.playerRef) throw new Error('Nincs játékos-aktor — a pálya nincs betöltve.')
    return this.playerRef
  }

  all(): readonly Actor[] {
    return this.list
  }

  get(id: EntityId): Actor | undefined {
    return this.byId.get(id)
  }

  spawn(spec: SpawnSpec): Actor {
    const health = spec.health ?? 100
    const a: Actor = {
      id: this.nextId++,
      kind: spec.kind,
      type: spec.type,
      faction: spec.faction,
      pos: new THREE.Vector3(spec.x, spec.y, spec.z),
      vel: new THREE.Vector3(),
      yaw: spec.yaw ?? 0,
      radius: spec.radius,
      height: spec.height,
      eyeHeight: spec.eyeHeight ?? spec.height * 0.85,
      health,
      maxHealth: health,
      alive: true,
      onGround: false,
      solid: spec.solid ?? true,
      vulnerable: spec.vulnerable ?? true,
      roomId: 0,
      deadFor: 0,
      age: 0,
      data: spec.data ?? null,
      anim: { telegraph: 0, pain: 0 },
      prevPos: new THREE.Vector3(spec.x, spec.y, spec.z),
      prevYaw: spec.yaw ?? 0,
      view: null,
    }
    this.list.push(a)
    this.byId.set(a.id, a)
    if (a.kind === 'player') this.playerRef = a
    this.events.emit('spawn', { actor: a })
    return a
  }

  remove(id: EntityId): void {
    const a = this.byId.get(id)
    if (!a) return
    this.byId.delete(id)
    const i = this.list.indexOf(a)
    if (i >= 0) this.list.splice(i, 1)
    if (a === this.playerRef) this.playerRef = null
  }

  clear(): void {
    this.list.length = 0
    this.byId.clear()
    this.playerRef = null
    this.nextId = 1
  }

  query(x: number, z: number, radius: number, faction: Faction | null, out: Actor[]): Actor[] {
    out.length = 0
    const r2 = radius * radius
    for (const a of this.list) {
      if (!a.alive) continue
      if (faction !== null && a.faction !== faction) continue
      const dx = a.pos.x - x
      const dz = a.pos.z - z
      if (dx * dx + dz * dz <= r2) out.push(a)
    }
    return out
  }

  raycast(
    origin: THREE.Vector3,
    dir: THREE.Vector3,
    maxDist: number,
    ignore: Actor | null,
    faction: Faction | null,
  ): ActorHit | null {
    let best: ActorHit | null = null

    for (const a of this.list) {
      if (a === ignore || !a.alive || !a.solid) continue
      if (faction !== null && a.faction !== faction) continue

      // Függőleges hengerrel metszünk: XZ-ben kör, Y-ban [pos.y, pos.y+height].
      const ox = origin.x - a.pos.x
      const oz = origin.z - a.pos.z
      const dxz2 = dir.x * dir.x + dir.z * dir.z
      if (dxz2 < 1e-9) continue

      const b = ox * dir.x + oz * dir.z
      const c = ox * ox + oz * oz - a.radius * a.radius
      const disc = b * b - dxz2 * c
      if (disc < 0) continue

      const sq = Math.sqrt(disc)
      let t = (-b - sq) / dxz2
      if (t < 0) t = (-b + sq) / dxz2
      if (t < 0 || t > maxDist) continue
      if (best && t >= best.distance) continue

      const y = origin.y + dir.y * t
      if (y < a.pos.y || y > a.pos.y + a.height) continue

      best = {
        actor: a,
        distance: t,
        point: new THREE.Vector3(origin.x + dir.x * t, y, origin.z + dir.z * t),
      }
    }
    return best
  }

  damage(
    target: Actor,
    amount: number,
    source: Actor | null,
    dir: THREE.Vector3 | null,
    kind: DamageKind,
  ): void {
    if (!target.alive || !target.vulnerable || amount <= 0) return

    let applied = Math.round(amount)

    // Páncél csak a játékosnál számít.
    if (target.kind === 'player' && this.armorHook) {
      const armor = this.armorHook.get()
      if (armor > 0) {
        const absorbed = Math.min(armor, Math.floor(applied * this.armorAbsorb))
        this.armorHook.consume(absorbed)
        applied -= absorbed
      }
    }

    target.health -= applied
    this.events.emit('damage', { target, amount: applied, source, kind, dir })

    if (target.health <= 0) {
      target.health = 0
      target.alive = false
      target.deadFor = 0
      target.solid = false
      this.events.emit('death', { actor: target, source })
      if (target.kind === 'player') {
        this.events.emit('playerDied', { by: source?.type ?? kind })
      }
    }
  }

  splash(
    center: THREE.Vector3,
    radius: number,
    maxDamage: number,
    source: Actor | null,
    kind: DamageKind,
    selfDamageFactor = 1,
  ): void {
    const r2 = radius * radius
    // Másolaton iterálunk: a halál-esemény kezelője új aktort szülhet.
    for (const a of this.list.slice()) {
      if (!a.alive || !a.vulnerable) continue

      // A robbanás a test közepét célozza, nem a lábát.
      _tmp.set(a.pos.x, a.pos.y + a.height * 0.5, a.pos.z)
      const d2 = _tmp.distanceToSquared(center)
      if (d2 > r2) continue

      // Takarás: ha nincs szabad látóvonal a robbanás közepétől a testig,
      // a fal felfogja. A fedezék MÖGÖTT nem érhet sebzés.
      if (this.lineOfSight && !this.lineOfSight(center, _tmp)) continue

      const d = Math.sqrt(d2)
      // Négyzetes csillapítás: a széle már alig sebez, a közepe teljesen.
      const falloff = 1 - (d / radius) ** 2
      let dmg = maxDamage * falloff
      if (a === source) dmg *= selfDamageFactor
      if (dmg < 1) continue

      _tmp.sub(center)
      if (_tmp.lengthSq() > 1e-6) _tmp.normalize()
      else _tmp.set(0, 1, 0)
      this.damage(a, dmg, source, _tmp.clone(), kind)
    }
  }

  /** Minden tick elején: az interpolációhoz elmentjük az előző állapotot. */
  snapshotPrev(): void {
    for (const a of this.list) {
      a.prevPos.copy(a.pos)
      a.prevYaw = a.yaw
      a.age++
      if (!a.alive) a.deadFor++
    }
  }
}
